/**
 * V2CardDrawer — detail view for a v2 card with version history + brand check.
 *
 * Opens from the Production Planner's card chips. Surfaces three panels:
 *   1. Card basics (title, stage, category, type, due_date, quality_score)
 *   2. Version history — each snapshot has a "Restore" button that creates a
 *      new version with the old payload (never overwrites the snapshot itself).
 *   3. Brand check — runs /cards/{id}/brand-check, shows drift_score + rationale +
 *      suggestions. Admin can tick "apply drift penalty" to push it into quality_score.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { Label } from "./ui/label";
import { Loader2, History, RotateCcw, Gauge, Shield, Send, Image as ImageIcon, Upload, X, AlertTriangle, Layers, Wand2, Trash2, Download, Link as LinkIcon, User, Save, Images, Shapes, Clock, ArrowRight, Copy, CheckCircle2 } from "lucide-react";
import { cleanCaption, deriveCaptionParts } from "../lib/captionClean";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { toast } from "sonner";
import ImageOverlayPanel from "./ImageOverlayPanel";
// VideoGenPanel import removed May 28 2026 — video-gen is parked.
// Restore by re-adding `import VideoGenPanel from "./VideoGenPanel";`
// alongside the commented JSX block below.
import ArtDirectorPanel from "./ArtDirectorPanel";
import StockFinderModal from "./StockFinderModal";
import AbstractDesignModal from "./AbstractDesignModal";
import CardPriorityBadge from "./CardPriorityBadge";
import EditorialSection from "./EditorialSection";
import CriticPanel from "./CriticPanel";
import { safeFetch } from "../lib/safeJson";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

const STAGE_BADGE = {
  BACKLOG: "bg-slate-100 text-slate-800 border-slate-300",
  READY_FOR_DESIGN: "bg-sky-100 text-sky-900 border-sky-300",
  IN_DESIGN: "bg-indigo-100 text-indigo-900 border-indigo-300",
  IN_REVIEW: "bg-violet-100 text-violet-900 border-violet-300",
  PENDING_APPROVAL: "bg-amber-100 text-amber-900 border-amber-300",
  APPROVED: "bg-emerald-100 text-emerald-900 border-emerald-300",
  REJECTED: "bg-red-100 text-red-900 border-red-300",
  SCHEDULED: "bg-cyan-100 text-cyan-900 border-cyan-300",
  PUBLISHED: "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-300",
  COMPLETED: "bg-slate-200 text-slate-800 border-slate-400",
};

// User-facing stage labels (mirror ProductionPlanner; internal keys unchanged).
const STAGE_LABEL = {
  BACKLOG: "Idea Stage",
  READY_FOR_DESIGN: "Ready for Design",
  IN_DESIGN: "In Design",
  IN_REVIEW: "In Review",
  PENDING_APPROVAL: "Awaiting Approval",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  SCHEDULED: "Scheduled",
  PUBLISHED: "Published",
  COMPLETED: "Completed",
  ARCHIVED: "Archived",
};

// "3d", "5h", "12m" — compact human duration since `iso`.
function durationSince(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60000));
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  if (days >= 1) return `${days}d`;
  if (hours >= 1) return `${hours}h`;
  return `${Math.max(1, mins)}m`;
}

export default function V2CardDrawer({ open, onOpenChange, workspaceId, cardId, user, onMutated }) {
  const [card, setCard] = useState(null);
  const [versions, setVersions] = useState([]);
  // REX-004D — approval audit timeline + latest decision banner.
  const [audit, setAudit] = useState({ events: [], latest_decision: null });
  // Jun 2026 — full stage-transition timeline (who moved it, when, from→to).
  const [stageHistory, setStageHistory] = useState({ history: [], stage_entered_at: null });
  const [showNextSteps, setShowNextSteps] = useState(false);
  const [brandCheck, setBrandCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);
  const [checking, setChecking] = useState(false);
  const [applyPenalty, setApplyPenalty] = useState(false);
  const [voices, setVoices] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [voiceKey, setVoiceKey] = useState("");
  const [marketKey, setMarketKey] = useState("");
  const [bufferChannels, setBufferChannels] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [publishMode, setPublishMode] = useState("addToQueue");
  const [publishDueAt, setPublishDueAt] = useState("");
  // Employee Post lifecycle (Feb 2026)
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [markingPosted, setMarkingPosted] = useState(false);

  const downloadEmployeeBundle = async () => {
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/employee-bundle`, {
        credentials: "include",
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e?.detail || `Bundle failed (${r.status})`);
      }
      const b = await r.json();
      // Build a self-contained text payload for LinkedIn pasting + image link.
      const textBlock = [
        b.title ? `# ${b.title}\n` : "",
        b.body_md || "",
        "",
        b.cta ? `${b.cta}` : "",
        "",
        (b.hashtags || []).map((h) => (h.startsWith("#") ? h : "#" + h)).join(" "),
        "",
        b.image_url ? `Image: ${b.image_url}` : "",
        b.posted_for_name ? `\nDrafted for: ${b.posted_for_name} <${b.posted_for_email || ""}>` : "",
      ].join("\n");
      const blob = new Blob([textBlock], { type: "text/plain;charset=utf-8" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `employee-post-${cardId.slice(0, 8)}.txt`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Bundle downloaded — paste into LinkedIn");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const markAsPosted = async () => {
    if (!card) return;
    if (!linkedinUrl || !linkedinUrl.toLowerCase().includes("linkedin.com")) {
      toast.error("Paste the LinkedIn post URL first.");
      return;
    }
    setMarkingPosted(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/mark-posted`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ linkedin_url: linkedinUrl, expected_version: card.version }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e?.detail || `Mark posted failed (${r.status})`);
      }
      toast.success("Marked as posted on LinkedIn — moved to PUBLISHED");
      setLinkedinUrl("");
      await load();
      onMutated && onMutated();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setMarkingPosted(false);
    }
  };
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showStockFinder, setShowStockFinder] = useState(false);
  const [showAbstract, setShowAbstract] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState("");
  const imageInputRef = useRef(null);
  const [platformVariants, setPlatformVariants] = useState({}); // {linkedin: {body, hashtags}, ...}
  const [variantDirty, setVariantDirty] = useState({});
  const [variantSaving, setVariantSaving] = useState(null);
  const [variantRewriting, setVariantRewriting] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const canDelete = user && ["admin", "leader", "owner"].includes((user.role || "").toLowerCase());

  const load = useCallback(async () => {
    if (!cardId) return;
    setLoading(true);
    try {
      const [cardR, versionsR, bcR, vR, mR, chR, auR, shR] = await Promise.all([
        fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}`, { credentials: "include" }),
        fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/versions`, { credentials: "include" }),
        fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/brand-check`, { credentials: "include" }),
        fetch(`${API}/brand-manager/voices`, { credentials: "include" }),
        fetch(`${API}/brand-manager/markets`, { credentials: "include" }),
        fetch(`${API}/buffer/channels`, { credentials: "include" }),
        fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/approval-audit`, { credentials: "include" }),
        fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/stage-history`, { credentials: "include" }),
      ]);
      const cardData = await cardR.json();
      setCard(cardData);
      // Hydrate per-platform variants from the card's platforms[] field
      const v = {};
      for (const p of cardData.platforms || []) {
        v[p.platform] = { body: p.body || "", hashtags: Array.isArray(p.hashtags) ? p.hashtags.join(" ") : "" };
      }
      setPlatformVariants(v);
      setVariantDirty({});
      setVersions(await versionsR.json());
      // REX-004D — load approval audit (best-effort; missing endpoint = empty).
      try {
        if (auR && auR.ok) {
          const au = await auR.json();
          setAudit({ events: au.events || [], latest_decision: au.latest_decision || null });
        } else {
          setAudit({ events: [], latest_decision: null });
        }
      } catch { setAudit({ events: [], latest_decision: null }); }
      // Jun 2026 — stage transition timeline (best-effort).
      try {
        if (shR && shR.ok) {
          const sh = await shR.json();
          setStageHistory({ history: sh.history || [], stage_entered_at: sh.stage_entered_at || null });
        } else {
          setStageHistory({ history: [], stage_entered_at: null });
        }
      } catch { setStageHistory({ history: [], stage_entered_at: null }); }
      const bc = await bcR.json();
      setBrandCheck(bc && Object.keys(bc).length ? bc : null);
      if (vR.ok) setVoices(await vR.json());
      if (mR.ok) setMarkets(await mR.json());
      if (chR.ok) {
        const chs = await chR.json();
        setBufferChannels(Array.isArray(chs) ? chs : []);
      }
    } catch (e) {
      toast.error("Failed to load card: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, cardId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const restoreVersion = async (v) => {
    if (!card) return;
    setRestoring(v);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/versions/${v}/restore`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ expected_version: card.version }),
        }
      );
      if (!r.ok) {
        const err = await r.json();
        throw new Error(typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail));
      }
      toast.success(`Restored v${v} → new v${card.version + 1}`);
      await load();
      onMutated && onMutated();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRestoring(null);
    }
  };

  const runBrandCheck = async () => {
    setChecking(true);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/brand-check?`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            apply_drift_penalty: applyPenalty,
            voice_key: voiceKey || null,
            market_key: marketKey || null,
          }),
        }
      );
      if (!r.ok) throw new Error(`Brand check failed (${r.status})`);
      const body = await r.json();
      setBrandCheck(body);
      toast.success(`Drift score ${(body.drift_score * 100).toFixed(0)}%`);
      if (applyPenalty) { await load(); onMutated && onMutated(); }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setChecking(false);
    }
  };

  // ── Message-consistency proof-read (warn-only) ──
  const [checkingConsistency, setCheckingConsistency] = useState(false);
  const consistency = card?.image_overlay?.consistency || null;
  const canAckConsistency = user && ["owner", "admin", "leader", "approver", "reviewer"].includes((user.role || "").toLowerCase());

  const runConsistency = async () => {
    setCheckingConsistency(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/consistency-check`, {
        method: "POST", credentials: "include",
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body.detail || `Check failed (${r.status})`);
      await load();
      if (body.verdict === "warn") toast.warning(`Proof-read flagged ${body.issues?.length || 0} issue(s) — review below`);
      else toast.success("Proof-read passed — graphic and copy look consistent");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCheckingConsistency(false);
    }
  };

  const ackConsistency = async () => {
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/consistency-ack`, {
        method: "POST", credentials: "include",
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body.detail || `Failed (${r.status})`);
      await load();
      toast.success("Warning acknowledged");
    } catch (e) {
      toast.error(e.message);
    }
  };


  const toggleChannel = (id) => {
    setSelectedChannels(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const IMAGE_REQUIRED_SERVICES = ["instagram", "pinterest", "tiktok"];

  const selectedServices = selectedChannels
    .map((id) => bufferChannels.find((c) => c.id === id)?.service)
    .filter(Boolean);

  const instagramSelected = selectedServices.some((s) =>
    IMAGE_REQUIRED_SERVICES.includes((s || "").toLowerCase())
  );

  const hasImage = !!(card && card.image_url);

  const [autoComposing, setAutoComposing] = useState(false);
  const [autoStatus, setAutoStatus] = useState("");
  const [variantOptions, setVariantOptions] = useState([]); // [{variant_id,label,rationale,image_url}]
  const [applyingVariant, setApplyingVariant] = useState(null); // variant_id being applied
  const [briefText, setBriefText] = useState(""); // optional art-direction for Rex
  // "Rex, design this" — one-tap agentic draft that returns 2-3 selectable
  // variants. Plans with the LLM, renders distinct treatments (cut-outs reused),
  // and polls the shared from-compose job; on done it shows a variant picker.
  const runAutoCompose = async () => {
    if (autoComposing) return;
    setAutoComposing(true);
    setVariantOptions([]);
    setAutoStatus("Rex is planning a few options…");
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/auto-compose`,
        { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ variants: 3, ...(briefText.trim() ? { brief: briefText.trim() } : {}) }) }
      );
      const started = await r.json().catch(() => ({}));
      if (!r.ok || !started.job_id) {
        toast.error(started?.detail || "Rex couldn't start the design");
        setAutoComposing(false); setAutoStatus(""); return;
      }
      setAutoStatus(started?.plan?.rationale || "Rendering 3 variants to choose from…");
      const deadline = Date.now() + 150000;
      let miss = 0;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 2000));
        let sr, sj;
        try {
          sr = await fetch(
            `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/from-compose-status/${started.job_id}`,
            { credentials: "include" }
          );
          sj = await sr.json().catch(() => ({}));
        } catch { miss += 1; if (miss > 8) break; continue; }
        if (sr.status === 404) {
          miss += 1;
          if (miss > 8) { toast.error("Lost track of Rex's design — please try again"); break; }
          continue;
        }
        miss = 0;
        if (sj.status === "done") {
          const vs = sj.variants || [];
          if (vs.length) {
            setVariantOptions(vs);
            if (sj.degraded || (sj.built && sj.built < (sj.requested || 3)))
              toast.message(`Rex drafted ${vs.length} option${vs.length > 1 ? "s" : ""} — a layer couldn't be saved; retry for the full set`);
            else
              toast.success(`Rex drafted ${vs.length} options — pick your favourite`);
          } else {
            toast.success("Rex designed your graphic — tweak it in the Director below");
            await load();
            onMutated && onMutated();
          }
          setAutoComposing(false); setAutoStatus(""); return;
        }
        if (sj.status === "error") {
          toast.error(sj?.detail || "Rex's compose failed");
          setAutoComposing(false); setAutoStatus(""); return;
        }
        if (sj.status === "processing") {
          setAutoStatus(briefText.trim()
            ? "Rendering your variants — generating & cutting layers (~30-60s)…"
            : "Rendering 3 variants…");
        }
      }
      toast.error("Rex's design timed out — please try again");
    } catch (e) {
      toast.error("Rex couldn't design this right now");
    } finally {
      setAutoComposing(false); setAutoStatus("");
    }
  };

  // Commit a chosen variant + run the VLM refine pass on it, then reload.
  const applyVariant = async (variantId) => {
    if (applyingVariant) return;
    setApplyingVariant(variantId);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/auto-compose/apply`,
        { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ variant_id: variantId, refine: true }) }
      );
      const started = await r.json().catch(() => ({}));
      if (!r.ok || !started.job_id) {
        toast.error(started?.detail || "Couldn't apply that variant");
        setApplyingVariant(null); return;
      }
      // The picked image is already live; refine just polishes it in the bg.
      const deadline = Date.now() + 90000;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 2000));
        const sr = await fetch(
          `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/from-compose-status/${started.job_id}`,
          { credentials: "include" }
        );
        const sj = await sr.json().catch(() => ({}));
        if (sj.status === "done" || sj.status === "error") break;
      }
      toast.success("Applied — tweak it in the Director below");
      setVariantOptions([]);
      await load();
      onMutated && onMutated();
    } catch (e) {
      toast.error("Couldn't apply that variant");
    } finally {
      setApplyingVariant(null);
    }
  };

  const uploadImageFile = async (fileObj) => {
    if (!fileObj) return;
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image`,
        { method: "POST", credentials: "include", body: fd }
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.detail || `Upload failed (${r.status})`);
      setCard((prev) => (prev ? { ...prev, image_url: body.image_url, image_plate_path: null, image_overlay: body.image_overlay ?? prev.image_overlay } : prev));
      toast.success("Image attached to card");
    } catch (e) {
      toast.error(e.message || "Image upload failed");
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const attachImageUrl = async () => {
    const url = imageUrlInput.trim();
    if (!url) { toast.error("Paste an image URL first"); return; }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("image_url", url);
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image`,
        { method: "POST", credentials: "include", body: fd }
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.detail || `Attach failed (${r.status})`);
      setCard((prev) => (prev ? { ...prev, image_url: body.image_url, image_plate_path: null, image_overlay: body.image_overlay ?? prev.image_overlay } : prev));
      setImageUrlInput("");
      toast.success("Image URL attached");
    } catch (e) {
      toast.error(e.message || "Attach failed");
    } finally {
      setUploadingImage(false);
    }
  };

  const clearImage = async () => {
    setUploadingImage(true);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image`,
        { method: "DELETE", credentials: "include" }
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.detail || `Remove failed (${r.status})`);
      }
      setCard((prev) => (prev ? { ...prev, image_url: null } : prev));
      toast.success("Image removed");
    } catch (e) {
      toast.error(e.message || "Remove failed");
    } finally {
      setUploadingImage(false);
    }
  };

  // Card-time stock attach (Architecture B). Sets the picked stock image as
  // the hero via select-candidate (stores the external URL as-is). Throws on
  // failure so the modal can surface it and stay open.
  const handleStockAttach = async (candidate) => {
    const r = await fetch(
      `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/select-candidate`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_url: candidate.full_url }),
      }
    );
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body?.detail || `Attach failed (${r.status})`);
    setCard((prev) => (prev ? { ...prev, image_url: candidate.full_url } : prev));
    toast.success("Stock image set as hero");
    onMutated && onMutated();
  };

  // Abstract design attach. The picked url is already a cards/... plate, so
  // select-candidate resolves + composes it immediately. Updates local state
  // with the composed result so the preview refreshes.
  const handleAbstractPick = async (url) => {
    const r = await fetch(
      `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/select-candidate`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_url: url }),
      }
    );
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body?.detail || `Apply failed (${r.status})`);
    setCard((prev) => (prev ? {
      ...prev,
      image_url: body.image_url || url,
      image_plate_path: body.plate_path || prev.image_plate_path,
      image_overlay: body.overlay || prev.image_overlay,
    } : prev));
    toast.success("Abstract design set as hero");
    onMutated && onMutated();
  };

  // Prefill the stock search from the card — stripped of test/prompt prefixes
  // and quotes. Operator edits before searching.
  const deriveStockQuery = () => {
    return (card?.title || "")
      .replace(/^(TEST_\S*|VOICETEST:?|VOICE_TEST:?|HEROPROMPT\S*)\s*/i, "")
      .replace(/["'\u2018\u2019\u201c\u201d]/g, "")
      .trim()
      .slice(0, 80);
  };

  const saveVariant = async (platform) => {
    const v = platformVariants[platform] || { body: "", hashtags: "" };
    setVariantSaving(platform);
    try {
      const hashtagsArr = (v.hashtags || "")
        .split(/\s+/)
        .map((t) => t.replace(/^#+/, ""))
        .filter(Boolean);
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/platforms/${platform}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ body: v.body || "", hashtags: hashtagsArr }),
        }
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.detail || `Save failed (${r.status})`);
      setVariantDirty((prev) => ({ ...prev, [platform]: false }));
      toast.success(`${platform} variant saved`);
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setVariantSaving(null);
    }
  };

  const clearVariant = async (platform) => {
    setVariantSaving(platform);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/platforms/${platform}`,
        { method: "DELETE", credentials: "include" }
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.detail || `Clear failed (${r.status})`);
      }
      setPlatformVariants((prev) => {
        const next = { ...prev };
        delete next[platform];
        return next;
      });
      setVariantDirty((prev) => ({ ...prev, [platform]: false }));
      toast.success(`${platform} variant cleared`);
    } catch (e) {
      toast.error(e.message || "Clear failed");
    } finally {
      setVariantSaving(null);
    }
  };

  const deleteCard = async () => {
    if (!window.confirm("Delete this card? It will be hidden from all views. Admins can restore within 30 days.")) return;
    setDeleting(true);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}`,
        { method: "DELETE", credentials: "include" }
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.detail || `Delete failed (${r.status})`);
      toast.success("Card deleted");
      onOpenChange && onOpenChange(false);
      onMutated && onMutated();
    } catch (e) {
      toast.error(e.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const rewriteVariant = async (platform) => {
    setVariantRewriting(platform);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/platforms/${platform}/rewrite`,
        { method: "POST", credentials: "include" }
      );      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.detail || `Rewrite failed (${r.status})`);
      setPlatformVariants((prev) => ({
        ...prev,
        [platform]: {
          body: body.body || "",
          hashtags: Array.isArray(body.hashtags) ? body.hashtags.join(" ") : "",
        },
      }));
      setVariantDirty((prev) => ({ ...prev, [platform]: false }));
      if (body.used_fallback) {
        toast.warning(`${platform}: Rex couldn't rewrite — kept the master body`);
      } else {
        toast.success(`Rex rewrote for ${platform}`);
      }
    } catch (e) {
      toast.error(e.message || "Rewrite failed");
    } finally {
      setVariantRewriting(null);
    }
  };

  // ── Refine master body with Rex ──
  const [refineInstruction, setRefineInstruction] = useState("");
  const [refining, setRefining] = useState(false);
  // ── Editable master body ──
  const [bodyDraft, setBodyDraft] = useState("");
  const [bodyDirty, setBodyDirty] = useState(false);
  const [savingBody, setSavingBody] = useState(false);
  // ── Graphic copy (headline + sub-header) — demarcated from the post caption ──
  const [gHeadline, setGHeadline] = useState("");
  const [gSubheader, setGSubheader] = useState("");
  const [graphicDirty, setGraphicDirty] = useState(false);
  const [savingGraphic, setSavingGraphic] = useState(false);

  useEffect(() => {
    setBodyDraft(card?.body_md || "");
    setBodyDirty(false);
    setGHeadline(card?.graphic_headline || "");
    setGSubheader(card?.graphic_subheader || "");
    setGraphicDirty(false);
  }, [card?.card_id, card?.version]);

  const saveGraphic = async () => {
    if (!graphicDirty || savingGraphic) return;
    setSavingGraphic(true);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ graphic_headline: gHeadline || null, graphic_subheader: gSubheader || null, expected_version: card?.version }),
        }
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.detail || `Save failed (${r.status})`);
      const updated = body?.card || body;
      setCard((prev) => (prev ? { ...prev, graphic_headline: gHeadline, graphic_subheader: gSubheader, version: updated.version ?? prev.version } : prev));
      setGraphicDirty(false);
      toast.success("Graphic copy saved");
      onMutated && onMutated();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSavingGraphic(false);
    }
  };

  const copyCleanCaption = async () => {
    const parts = deriveCaptionParts({ ...card, body_md: bodyDraft, graphic_headline: gHeadline, graphic_subheader: gSubheader });
    const clean = parts.caption || cleanCaption(bodyDraft);
    try {
      await navigator.clipboard.writeText(clean);
      toast.success("Clean caption copied — ready to publish");
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  const saveBody = async () => {
    if (!bodyDirty || savingBody) return;
    setSavingBody(true);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ body_md: bodyDraft, expected_version: card?.version }),
        }
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.detail || `Save failed (${r.status})`);
      const updated = body?.card || body;
      setCard((prev) => (prev ? { ...prev, body_md: updated.body_md ?? bodyDraft, version: updated.version ?? prev.version } : prev));
      setBodyDirty(false);
      toast.success("Body saved");
      onMutated && onMutated();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSavingBody(false);
    }
  };


  const refineBody = async () => {
    const instr = refineInstruction.trim();
    if (!instr || refining) return;
    setRefining(true);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/refine`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ instruction: instr, current_body: bodyDraft || card?.body_md || "" }),
        }
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.detail || `Refine failed (${r.status})`);
      if (body.used_fallback) {
        toast.warning("Rex couldn't refine — kept the current body");
      } else {
        setCard((prev) => (prev ? { ...prev, body_md: body.body, version: body.version || prev.version } : prev));
        setBodyDraft(body.body);
        setBodyDirty(false);
        setRefineInstruction("");
        toast.success("Rex refined the post");
        onMutated && onMutated();
      }
    } catch (e) {
      toast.error(e.message || "Refine failed");
    } finally {
      setRefining(false);
    }
  };

  const publishToBuffer = async () => {
    if (selectedChannels.length === 0) {
      toast.error("Pick at least one channel");
      return;
    }
    if (instagramSelected && !hasImage) {
      toast.error("Instagram requires an image — attach one below before publishing.");
      return;
    }
    if (publishMode === "customScheduled" && !publishDueAt) {
      toast.error("Pick a date/time for scheduled posts");
      return;
    }
    setPublishing(true);
    setPublishResult(null);
    try {
      const body = {
        channel_ids: selectedChannels,
        mode: publishMode,
      };
      if (publishMode === "customScheduled") {
        // Convert local datetime input to UTC ISO 8601
        body.due_at = new Date(publishDueAt).toISOString();
      }
      // REX-AUTH-RELIABILITY (ported from parallel fork iter-243, May 2026):
      // use safeFetch so an HTML 502/maintenance page from the proxy never
      // crashes this flow with "Unexpected token '<'". safeFetch surfaces a
      // toast-ready error string and a clean `{ok, status, data}` shape.
      const { ok, status, data, error } = await safeFetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/buffer-publish`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      const out = data || {};
      setPublishResult(out);
      if (ok && out.ok) {
        const okCount = (out.results || []).filter(x => x.success).length;
        const fail = (out.results || []).length - okCount;
        toast.success(`Sent to Buffer: ${okCount} ok${fail ? `, ${fail} failed` : ""}`);
      } else {
        const perChannel = (out.results || []).map(r => r.error).filter(Boolean).join("; ");
        toast.error(out.detail || perChannel || error || `Publish failed (status ${status})`);
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="v2-card-drawer">
        <SheetHeader>
          <div className="flex items-start justify-between pr-6">
            <SheetTitle className="text-slate-900">Card details</SheetTitle>
            {canDelete && card && (
              <Button
                size="sm"
                variant="ghost"
                onClick={deleteCard}
                disabled={deleting}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="drawer-delete-card-btn"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin"/> : <><Trash2 className="h-4 w-4 mr-1"/>Delete</>}
              </Button>
            )}
          </div>
          {/* aria-describedby target — Radix's SheetContent wires this
              automatically when a SheetDescription is present. The
              sr-only class keeps it accessible to screen readers
              without taking visual space in the drawer header. */}
          <SheetDescription className="sr-only">
            Edit card metadata, view Art Director recommendations,
            manage editorial register and accents, and run the VLM
            Critic against this card.
          </SheetDescription>
        </SheetHeader>

        {loading && <Skeleton className="h-64 mt-4"/>}

        {card && !loading && (
          <div className="space-y-6 mt-4">
            {/* Basics */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={STAGE_BADGE[card.stage] || ""}>{card.stage}</Badge>
                <Badge variant="outline" className="border-slate-300 text-slate-700">v{card.version}</Badge>
                {typeof card.quality_score === "number" && (
                  <Badge className="bg-fuchsia-100 text-fuchsia-900 border-fuchsia-300">
                    <Gauge className="h-3 w-3 mr-1"/>Q {card.quality_score}
                  </Badge>
                )}
                <CardPriorityBadge
                  card={card}
                  workspaceId={workspaceId}
                  size="md"
                  onChange={(updated) => {
                    setCard((prev) => prev ? { ...prev, priority: updated.priority, version: updated.version ?? prev.version } : prev);
                    onMutated && onMutated();
                  }}
                />
              </div>
              <h3 className="text-xl font-semibold text-slate-900" data-testid="drawer-title">{card.title}</h3>
              <p className="text-xs text-slate-500">{card.category} · {card.card_type} · due {card.due_date || "—"}</p>

              {/* Graphic copy — headline + sub-header the DESIGNER puts into the
                  graphic. Demarcated from the post caption below. */}
              <div className="space-y-2 rounded-lg border border-teal-200 bg-teal-50/60 p-3" data-testid="drawer-graphic-copy">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-teal-800">Graphic copy — for the design</label>
                  {graphicDirty && (
                    <Button
                      size="sm"
                      onClick={saveGraphic}
                      disabled={savingGraphic}
                      className="bg-teal-600 hover:bg-teal-700 text-white h-7 text-xs"
                      data-testid="drawer-graphic-save-btn"
                    >
                      {savingGraphic ? <Loader2 className="h-3 w-3 animate-spin"/> : <><Save className="h-3 w-3 mr-1"/>Save</>}
                    </Button>
                  )}
                </div>
                <div>
                  <label className="block text-[10px] text-slate-600 mb-1">Headline</label>
                  <Input
                    value={gHeadline}
                    onChange={(e) => { setGHeadline(e.target.value); setGraphicDirty(true); }}
                    placeholder="Punchy headline for the graphic"
                    className="text-sm bg-white"
                    data-testid="drawer-graphic-headline"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-600 mb-1">Sub-header</label>
                  <Input
                    value={gSubheader}
                    onChange={(e) => { setGSubheader(e.target.value); setGraphicDirty(true); }}
                    placeholder="Short supporting line for the graphic"
                    className="text-sm bg-white"
                    data-testid="drawer-graphic-subheader"
                  />
                </div>
                {deriveCaptionParts(card).legacy && (gHeadline || gSubheader) && (
                  <p className="text-[10px] text-teal-700">Auto-detected from the post — edit and Save to lock it in.</p>
                )}
              </div>

              {/* Body — editable. Always render so users can author from
                  scratch even when the wizard didn't seed anything. */}
              <div className="space-y-2" data-testid="drawer-body-section">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Post caption</label>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={copyCleanCaption}
                      className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid="drawer-copy-clean-caption-btn"
                    >
                      <Copy className="h-3 w-3 mr-1"/>Copy clean caption
                    </Button>
                    {bodyDirty && (
                      <Button
                        size="sm"
                        onClick={saveBody}
                        disabled={savingBody}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                        data-testid="drawer-body-save-btn"
                      >
                        {savingBody ? <Loader2 className="h-3 w-3 animate-spin"/> : <><Save className="h-3 w-3 mr-1"/>Save body</>}
                      </Button>
                    )}
                  </div>
                </div>
                <Textarea
                  value={bodyDraft}
                  onChange={(e) => { setBodyDraft(e.target.value); setBodyDirty(e.target.value !== (card.body_md || "")); }}
                  rows={10}
                  placeholder="Write or paste your post copy here…"
                  className="font-mono text-xs"
                  data-testid="drawer-body-textarea"
                />
                {bodyDirty && (
                  <p className="text-[10px] text-amber-700">Unsaved changes — click "Save body" to persist.</p>
                )}
                <div className="flex items-end gap-2 pt-1">
                  <div className="flex-1">
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-purple-700 mb-1">
                      Refine with Rex
                    </label>
                    <input
                      type="text"
                      value={refineInstruction}
                      onChange={(e) => setRefineInstruction(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey && refineInstruction.trim() && !refining) {
                          e.preventDefault();
                          refineBody();
                        }
                      }}
                      placeholder="e.g. make this more celebratory for May Day"
                      disabled={refining || !bodyDraft.trim()}
                      className="w-full text-sm rounded-md border border-purple-200 bg-white px-3 py-1.5 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-400"
                      data-testid="drawer-refine-input"
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={refineBody}
                    disabled={refining || !refineInstruction.trim() || !bodyDraft.trim()}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="drawer-refine-btn"
                  >
                    {refining ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="h-3.5 w-3.5 mr-1" />Refine</>}
                  </Button>
                </div>
                <p className="text-[10px] text-slate-500">
                  Edit directly above, or ask Rex to rewrite via Claude (~$0.003). Refine saves the new body automatically. Platform variants reset — click "Rex rewrite" on each to re-generate.
                </p>
              </div>
            </div>

            {/* Phase 4 — Editorial section (register, accents, accent-word picker) */}
            <EditorialSection
              card={card}
              workspaceId={workspaceId}
              cardId={cardId}
              onMutated={onMutated}
              setCard={setCard}
              API={API}
            />

            {/* Phase 5 Day 3 — VLM Critic panel (skeleton; pre-calibration framing) */}
            <CriticPanel card={card} workspaceId={workspaceId} />

            {/* Employee Post lifecycle panel — only for post_type='employee' */}
            {card.post_type === "employee" && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3" data-testid="drawer-employee-panel">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-amber-700" />
                  <h4 className="text-sm font-semibold text-amber-900">Employee Post</h4>
                </div>
                <p className="text-xs text-amber-900">
                  Drafted for <span className="font-semibold">{card.posted_for_name || card.posted_for_email}</span>.
                  This post does not auto-publish — they download the bundle and post it from their own LinkedIn.
                </p>

                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={downloadEmployeeBundle}
                    className="border-amber-300 text-amber-900 hover:bg-amber-100"
                    data-testid="employee-download-bundle-btn"
                  >
                    <Download className="h-4 w-4 mr-1.5" /> Download bundle
                  </Button>
                </div>

                {card.stage !== "PUBLISHED" && (
                  <div className="space-y-2 pt-2 border-t border-amber-200">
                    <Label className="text-xs text-amber-900">I posted it — paste LinkedIn URL</Label>
                    <div className="flex gap-2">
                      <input
                        type="url"
                        placeholder="https://www.linkedin.com/posts/..."
                        value={linkedinUrl}
                        onChange={(e) => setLinkedinUrl(e.target.value)}
                        className="flex-1 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs text-slate-900"
                        data-testid="employee-linkedin-url-input"
                      />
                      <Button
                        size="sm"
                        onClick={markAsPosted}
                        disabled={markingPosted}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="employee-mark-posted-btn"
                      >
                        {markingPosted ? <Loader2 className="h-3 w-3 animate-spin" /> : <><LinkIcon className="h-3 w-3 mr-1" />Mark posted</>}
                      </Button>
                    </div>
                  </div>
                )}

                {card.stage === "PUBLISHED" && (
                  <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2" data-testid="employee-published-state">
                    Published. {(card.platforms || []).filter(p => p.platform === "linkedin").map(p => (
                      <a key={p.external_post_id} href={p.external_post_id} target="_blank" rel="noreferrer" className="underline ml-1">View on LinkedIn</a>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Brand manual check */}
            <div className="space-y-2" data-testid="drawer-brand-check">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Shield className="h-4 w-4"/>Brand Manual check</h4>
                <Button size="sm" onClick={runBrandCheck} disabled={checking} data-testid="drawer-run-brand-check-btn">
                  {checking ? <Loader2 className="h-3 w-3 animate-spin"/> : "Run manual check"}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] text-slate-500">Voice</Label>
                  <select value={voiceKey} onChange={(e) => setVoiceKey(e.target.value)} data-testid="drawer-voice-select"
                    className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900">
                    <option value="">— None —</option>
                    {voices.map(v => <option key={v.key} value={v.key}>{v.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[11px] text-slate-500">Market</Label>
                  <select value={marketKey} onChange={(e) => setMarketKey(e.target.value)} data-testid="drawer-market-select"
                    className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900">
                    <option value="">— None —</option>
                    {markets.map(m => <option key={m.key} value={m.key}>{m.name}</option>)}
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-1 text-xs text-slate-600">
                <input type="checkbox" checked={applyPenalty} onChange={(e) => setApplyPenalty(e.target.checked)} data-testid="drawer-apply-penalty-checkbox" />
                Apply drift penalty to quality score
              </label>
              {brandCheck ? (
                <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <ManualVerdictBadge score={brandCheck.drift_score} />
                    <DriftBadge score={brandCheck.drift_score}/>
                    {brandCheck.used_fallback && <Badge className="bg-amber-100 text-amber-900 border-amber-300">heuristic</Badge>}
                    {brandCheck.applied_drift_penalty != null && (
                      <Badge variant="outline" className="border-slate-300 text-slate-700">
                        −{brandCheck.applied_drift_penalty} pts applied
                      </Badge>
                    )}
                  </div>
                  {brandCheck.rationale && <p className="text-slate-700 mb-2 text-xs">{brandCheck.rationale}</p>}
                  {brandCheck.suggestions?.length > 0 && (
                    <ul className="list-disc pl-5 text-slate-700 space-y-1 text-xs">
                      {brandCheck.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-500">Not checked yet. Pick voice + market for best results, then Run. Checks capitalisation rules, voice anti-drift, market cultural don'ts, and Brand Manual "Do Not Use".</p>
              )}
            </div>

            {/* Message consistency (warn-only proof-read) */}
            <div className="space-y-2 rounded-md border border-slate-200 bg-white p-3" data-testid="drawer-consistency">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <ImageIcon className="h-4 w-4"/> Proof-read (graphic vs copy)
                </h4>
                <div className="flex items-center gap-2">
                  {consistency?.status === "warn" && <Badge className="bg-amber-100 text-amber-900 border-amber-300" data-testid="consistency-badge-warn">⚠ {consistency.issues?.length || 0} flagged</Badge>}
                  {consistency?.status === "pass" && <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300" data-testid="consistency-badge-pass">✓ Consistent</Badge>}
                  <Button size="sm" variant="outline" onClick={runConsistency} disabled={checkingConsistency || !hasImage} data-testid="run-consistency-btn">
                    {checkingConsistency ? "Checking…" : "Run proof-read"}
                  </Button>
                </div>
              </div>
              {!hasImage && <p className="text-xs text-slate-500">Attach or generate an image first.</p>}
              {consistency ? (
                <div className="text-xs" data-testid="consistency-result">
                  {consistency.rationale && <p className="text-slate-700 mb-1">{consistency.rationale}</p>}
                  {(consistency.issues || []).length > 0 ? (
                    <ul className="space-y-1.5">
                      {consistency.issues.map((it, i) => (
                        <li key={i} className="rounded border border-amber-200 bg-amber-50 p-2" data-testid={`consistency-issue-${i}`}>
                          <div className="font-medium text-amber-900">{it.question || it.why}</div>
                          {(it.graphic_says || it.copy_says) && (
                            <div className="text-slate-600 mt-0.5">
                              {it.graphic_says && <>Graphic: <span className="font-mono">“{it.graphic_says}”</span></>}
                              {it.graphic_says && it.copy_says && <span> · </span>}
                              {it.copy_says && <>Copy: <span className="font-mono">“{it.copy_says}”</span></>}
                            </div>
                          )}
                          <span className="inline-block mt-0.5 text-[10px] uppercase tracking-wide text-amber-700">{it.severity} · {it.type}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-emerald-700">No issues found.</p>
                  )}
                  {consistency.degraded && <p className="text-slate-400 mt-1">Check skipped ({consistency.degraded}).</p>}
                  {consistency.status === "warn" && (
                    consistency.ack_by ? (
                      <p className="text-slate-500 mt-2" data-testid="consistency-acked">Acknowledged by {consistency.ack_by}.</p>
                    ) : canAckConsistency ? (
                      <Button size="sm" variant="outline" className="mt-2" onClick={ackConsistency} data-testid="ack-consistency-btn">Acknowledge & proceed</Button>
                    ) : (
                      <p className="text-slate-500 mt-2">Only an approver can acknowledge this warning.</p>
                    )
                  )}
                </div>
              ) : (
                hasImage && <p className="text-xs text-slate-500">Not proof-read yet. Runs automatically after an upload; or Run it now. Checks that the graphic's text matches the caption and that nothing is wrong or out of date.</p>
              )}
            </div>

            {/* Hero image (mandatory for Instagram/Pinterest/TikTok) */}
            <div className="space-y-2 rounded-md border border-slate-200 bg-white p-3" data-testid="drawer-card-image">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <ImageIcon className="h-4 w-4"/> Hero image
                </h4>
                <div className="flex items-center gap-1">
                  {hasImage && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={clearImage}
                      disabled={uploadingImage}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid="drawer-remove-image-btn"
                    >
                      <X className="h-3 w-3 mr-1"/> Remove
                    </Button>
                  )}
                </div>
              </div>
              {/* Phase 3 — Art Director inline panel. Drives the REAL
                  recommender (retrieval → heuristics → LLM) + audit commit. */}
              {card.card_type !== "COVER_DESIGN" && (
                <div className="rounded-md border border-indigo-200 bg-indigo-50/60 p-3 mb-2" data-testid="rex-auto-compose">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-indigo-900 flex items-center gap-1.5">
                        <Wand2 className="h-4 w-4" /> Rex, design this
                      </div>
                      <p className="text-[11px] text-indigo-700/80 mt-0.5">
                        One tap — Rex picks a layout, cuts out a subject, adds a shadow and styles the headline. Tweak it after in the Director.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={runAutoCompose}
                      disabled={autoComposing}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
                      data-testid="rex-auto-compose-btn"
                    >
                      {autoComposing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Wand2 className="h-3.5 w-3.5 mr-1.5" />Design it</>}
                    </Button>
                  </div>
                  <textarea
                    value={briefText}
                    onChange={(e) => setBriefText(e.target.value)}
                    disabled={autoComposing}
                    rows={2}
                    placeholder="Optional brief for Rex — e.g. 'a jigsaw puzzle coming together, a cut-out of a facilitator on the right, DISC Lens logo bottom-right'. Rex can generate elements, cut stock props, use your subjects & logos."
                    className="mt-2 w-full text-[11px] rounded-md border border-indigo-200 bg-white/80 p-2 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 resize-none"
                    data-testid="rex-brief-input"
                  />
                  {autoComposing && autoStatus && (
                    <div className="mt-2 flex items-center gap-1.5 text-[11px] text-indigo-700" data-testid="rex-auto-compose-status">
                      <Loader2 className="h-3 w-3 animate-spin" /> {autoStatus}
                    </div>
                  )}
                  {variantOptions.length > 0 && (
                    <div className="mt-3" data-testid="rex-variant-picker">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="text-[11px] font-semibold text-indigo-900">
                          Pick a variant — Rex will polish your choice
                        </div>
                        <button
                          type="button"
                          onClick={() => setVariantOptions([])}
                          disabled={!!applyingVariant}
                          className="text-[10px] text-slate-500 hover:text-indigo-700 disabled:opacity-50"
                          data-testid="rex-variant-picker-dismiss"
                        >
                          Dismiss
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {variantOptions.map((v) => (
                          <button
                            key={v.variant_id}
                            type="button"
                            onClick={() => applyVariant(v.variant_id)}
                            disabled={!!applyingVariant}
                            className="group text-left rounded-md border border-indigo-200 bg-white overflow-hidden hover:border-indigo-500 hover:ring-2 hover:ring-indigo-300 transition-colors disabled:opacity-60"
                            data-testid={`rex-variant-${v.variant_id}`}
                          >
                            <div className="relative aspect-square bg-slate-100">
                              {v.image_url ? (
                                <img src={v.image_url} alt={v.label} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">…</div>
                              )}
                              {applyingVariant === v.variant_id && (
                                <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                                  <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
                                </div>
                              )}
                            </div>
                            <div className="p-1.5">
                              <div className="text-[11px] font-semibold text-indigo-900 leading-tight">{v.label}</div>
                              <div className="text-[10px] text-slate-500 leading-snug mt-0.5 line-clamp-2">{v.rationale}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <ArtDirectorPanel
                workspaceId={workspaceId}
                cardId={cardId}
                card={card}
                onPickCommitted={(pick) => {
                  setCard((prev) => (prev ? { ...prev, image_url: pick.asset_thumbnail_url } : prev));
                  onMutated && onMutated();
                }}
              />
              {showStockFinder && (
                <StockFinderModal
                  workspaceId={workspaceId}
                  initialQuery={deriveStockQuery()}
                  onAttach={handleStockAttach}
                  onClose={() => setShowStockFinder(false)}
                />
              )}
              {showAbstract && (
                <AbstractDesignModal
                  workspaceId={workspaceId}
                  cardId={cardId}
                  onPick={handleAbstractPick}
                  onClose={() => setShowAbstract(false)}
                />
              )}
              {/* Secondary manual options. The Art Director above is the
                  primary path — these are fallbacks for choosing by hand. */}
              <div className="pt-1" data-testid="drawer-image-secondary-options">
                <p className="text-[11px] text-slate-500 mb-1.5">
                  Prefer to choose yourself? Search real stock photography, pick an abstract brand design, or upload your own image below.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowStockFinder(true)}
                    disabled={uploadingImage}
                    data-testid="drawer-find-stock-btn"
                    title="Find a stock photo (Unsplash + Pexels) for this card"
                  >
                    <Images className="h-3 w-3 mr-1" /> Find stock photo
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowAbstract(true)}
                    disabled={uploadingImage}
                    data-testid="drawer-abstract-btn"
                    title="Pick an on-brand abstract background design"
                  >
                    <Shapes className="h-3 w-3 mr-1" /> Abstract design
                  </Button>
                </div>
              </div>
              {hasImage ? (
                <div className="space-y-2">
                  <ImageOverlayPanel
                    workspaceId={workspaceId}
                    cardId={cardId}
                    card={card}
                    onComposed={(body) => {
                      if (!body) return;
                      setCard((prev) => (prev ? { ...prev, image_url: body.image_url, image_overlay: body.overlay, image_plate_path: body.plate_path } : prev));
                      onMutated && onMutated();
                    }}
                  />
                  <p className="text-[11px] text-slate-500 truncate" data-testid="drawer-image-url">{card.image_url}</p>
                  {card.image_overlay?.image_provenance && (
                    card.image_overlay.image_provenance === "rex_designed" ? (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded px-2 py-[2px] border bg-indigo-100 text-indigo-800 border-indigo-300"
                        data-testid="drawer-provenance-badge"
                        title="This graphic was designed in Rex"
                      >
                        <Wand2 className="h-3 w-3" /> REX Designed
                      </span>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded px-2 py-[2px] border bg-emerald-50 text-emerald-800 border-emerald-300"
                          data-testid="drawer-provenance-badge"
                          title="This graphic was manually uploaded — Rex's background tasks won't overwrite it"
                        >
                          <Upload className="h-3 w-3" /> Your upload — Rex won't touch this
                        </span>
                        <button
                          type="button"
                          onClick={runAutoCompose}
                          disabled={autoComposing}
                          className="inline-flex items-center gap-1 h-6 px-2 text-[10px] font-semibold rounded border border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                          data-testid="drawer-let-rex-redesign-btn"
                          title="Hand this card back to Rex to generate fresh AI-designed variants"
                        >
                          {autoComposing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} Let Rex redesign
                        </button>
                      </div>
                    )
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        try {
                          const u = new URL(card.image_url, window.location.origin);
                          // Add download flag + a sensible filename so the browser
                          // saves the composed PNG instead of opening it inline.
                          u.searchParams.set("download", "true");
                          const safeTitle = (card.title || "amplify-card")
                            .replace(/[^a-zA-Z0-9-_ ]/g, "")
                            .trim()
                            .replace(/\s+/g, "-")
                            .slice(0, 60) || "amplify-card";
                          const fmt = card.image_overlay?.format || "square_1080";
                          u.searchParams.set("filename", `${safeTitle}__${fmt}.png`);
                          const a = document.createElement("a");
                          a.href = u.toString();
                          a.download = `${safeTitle}__${fmt}.png`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                        } catch (e) {
                          window.open(card.image_url, "_blank");
                        }
                      }}
                      className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                      data-testid="drawer-download-png"
                      title="Download composed PNG"
                    >
                      ⬇ Download PNG
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const safeTitle = (card.title || "cover")
                          .replace(/[^a-zA-Z0-9-_ ]/g, "").trim().replace(/\s+/g, "-").slice(0, 60) || "cover";
                        const a = document.createElement("a");
                        a.href = `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/pdf`;
                        a.download = `${safeTitle}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      }}
                      className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                      data-testid="drawer-download-pdf"
                      title="Download composed image as PDF"
                    >
                      ⬇ Download PDF
                    </button>
                  </div>
                  <VisualBrandCheck workspaceId={workspaceId} cardId={cardId} />
                  {card.card_type === "COVER_DESIGN" && card.stage === "IN_DESIGN" && (
                    <Button
                      size="sm"
                      onClick={async () => {
                        const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/transition`, {
                          method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ target_stage: "PENDING_APPROVAL", expected_version: card.version }),
                        });
                        if (r.ok) { toast.success("Sent for approval — moved to Awaiting Approval"); load(); onMutated && onMutated(); }
                        else { const e = await r.json().catch(() => ({})); toast.error(e?.detail || "Couldn't send for approval"); }
                      }}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="cover-send-for-approval-btn"
                      title="Mark this cover complete and send it for approval"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Complete &amp; send for approval
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-500">
                  No image attached. <span className="font-semibold">Instagram, Pinterest, and TikTok posts will be blocked</span> until you attach a hero image.
                </p>
              )}
              <div className="grid grid-cols-1 gap-2 pt-1">
                <div>
                  <Label className="text-[11px] text-slate-500">Upload file (max 10 MB, PNG/JPG)</Label>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => uploadImageFile(e.target.files?.[0])}
                    disabled={uploadingImage}
                    className="mt-1 w-full text-xs"
                    data-testid="drawer-image-file-input"
                  />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-500">Or paste an image URL</Label>
                  <div className="flex gap-1.5 mt-1">
                    <input
                      type="url"
                      placeholder="https://…"
                      value={imageUrlInput}
                      onChange={(e) => setImageUrlInput(e.target.value)}
                      className="flex-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs"
                      data-testid="drawer-image-url-input"
                    />
                    <Button
                      size="sm"
                      onClick={attachImageUrl}
                      disabled={uploadingImage || !imageUrlInput.trim()}
                      data-testid="drawer-attach-image-url-btn"
                    >
                      {uploadingImage ? <Loader2 className="h-3 w-3 animate-spin"/> : <Upload className="h-3 w-3"/>}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Video — VideoGen.io
              * QUARANTINED Feb 2026 per /app/memory/VIDEO_GEN_PARKED.md.
              * Component intentionally not rendered AND import removed
              * (was triggering unused-import noise). Restore by
              * re-importing VideoGenPanel and uncommenting the block
              * below, plus re-enabling routes in videogen_routes.py.
              */}
            {/*
            <VideoGenPanel
              workspaceId={workspaceId}
              cardId={cardId}
              card={card}
              API={API}
              onUpdated={() => onMutated && onMutated()}
            />
            */}

            {/* Posting-related tools (per-platform copy, Buffer, analytics) — hidden for covers */}
            {card.card_type !== "COVER_DESIGN" && (
            <>
            {/* Per-platform copy variants */}
            <div className="space-y-2 rounded-md border border-slate-200 bg-white p-3" data-testid="drawer-platform-variants">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <Layers className="h-4 w-4"/> Per-platform copy
                </h4>
                <span className="text-[11px] text-slate-500">Overrides the master body on publish</span>
              </div>
              <p className="text-[11px] text-slate-500">
                Leave a platform blank to use the master body above. LinkedIn tolerates ~3000 chars; Instagram caps captions at ~2200; Twitter/X at 280.
              </p>
              {["linkedin", "instagram", "twitter", "facebook"].map((plat) => {
                const v = platformVariants[plat] || { body: "", hashtags: "" };
                const isDirty = !!variantDirty[plat];
                const hasVariant = (v.body || "").trim().length > 0 || (v.hashtags || "").trim().length > 0;
                return (
                  <div key={plat} className="rounded border border-slate-200 p-2 space-y-1.5" data-testid={`variant-${plat}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-700 capitalize">{plat}</span>
                        {hasVariant && !isDirty && (
                          <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300 text-[10px]">saved</Badge>
                        )}
                        {isDirty && (
                          <Badge className="bg-amber-100 text-amber-900 border-amber-300 text-[10px]">unsaved</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => rewriteVariant(plat)}
                          disabled={variantRewriting === plat || variantSaving === plat}
                          data-testid={`variant-rewrite-${plat}-btn`}
                          title="Rex rewrites the master body for this platform (~$0.003)"
                        >
                          {variantRewriting === plat
                            ? <Loader2 className="h-3 w-3 animate-spin"/>
                            : <><Wand2 className="h-3 w-3 mr-1"/>Rex rewrite</>}
                        </Button>
                        {hasVariant && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => clearVariant(plat)}
                            disabled={variantSaving === plat}
                            data-testid={`variant-clear-${plat}-btn`}
                          >
                            <X className="h-3 w-3"/>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => saveVariant(plat)}
                          disabled={!isDirty || variantSaving === plat || (!hasVariant && !(v.body || "").trim() && !(v.hashtags || "").trim())}
                          data-testid={`variant-save-${plat}-btn`}
                        >
                          {variantSaving === plat ? <Loader2 className="h-3 w-3 animate-spin"/> : "Save"}
                        </Button>
                      </div>
                    </div>
                    <textarea
                      value={v.body || ""}
                      onChange={(e) => {
                        setPlatformVariants((prev) => ({ ...prev, [plat]: { ...(prev[plat] || {}), body: e.target.value } }));
                        setVariantDirty((prev) => ({ ...prev, [plat]: true }));
                      }}
                      placeholder={`${plat} body (leave blank to use master)`}
                      rows={3}
                      className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-800"
                      data-testid={`variant-body-${plat}`}
                    />
                    <input
                      type="text"
                      value={v.hashtags || ""}
                      onChange={(e) => {
                        setPlatformVariants((prev) => ({ ...prev, [plat]: { ...(prev[plat] || {}), hashtags: e.target.value } }));
                        setVariantDirty((prev) => ({ ...prev, [plat]: true }));
                      }}
                      placeholder="hashtags (space-separated, no # needed)"
                      className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-800"
                      data-testid={`variant-hashtags-${plat}`}
                    />
                  </div>
                );
              })}
            </div>

            {/* Buffer publish */}
            <div className="space-y-2 rounded-md border border-slate-200 bg-white p-3" data-testid="drawer-buffer-publish">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <Send className="h-4 w-4"/> Publish to LinkedIn (via Buffer)
                </h4>
                <Button size="sm" onClick={publishToBuffer} disabled={publishing || selectedChannels.length === 0 || (instagramSelected && !hasImage)} data-testid="drawer-publish-buffer-btn">
                  {publishing ? <Loader2 className="h-3 w-3 animate-spin"/> : "Send to Buffer"}
                </Button>
              </div>
              {instagramSelected && !hasImage && (
                <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900" data-testid="drawer-ig-image-warning">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0"/>
                  <span>
                    Instagram rejects text-only posts. Attach a hero image above to publish to Instagram.
                  </span>
                </div>
              )}
              {bufferChannels.length === 0 ? (
                <p className="text-xs text-slate-500">No Buffer channels found. Connect a channel in Buffer first.</p>
              ) : (
                <>
                  <div className="space-y-1">
                    <Label className="text-[11px] text-slate-500">Channels</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {[...bufferChannels].sort((a, b) => {
                        // LinkedIn first, then alpha by service
                        if (a.service === "linkedin" && b.service !== "linkedin") return -1;
                        if (b.service === "linkedin" && a.service !== "linkedin") return 1;
                        return (a.service || "").localeCompare(b.service || "");
                      }).map(c => (
                        <button
                          key={c.id}
                          onClick={() => toggleChannel(c.id)}
                          className={`text-xs rounded-full px-2.5 py-1 border transition ${
                            selectedChannels.includes(c.id)
                              ? "bg-indigo-600 text-white border-indigo-600"
                              : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                          }`}
                          data-testid={`buffer-channel-${c.id}`}
                        >
                          {c.service} · {c.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] text-slate-500">Mode</Label>
                      <select
                        value={publishMode}
                        onChange={(e) => setPublishMode(e.target.value)}
                        className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs"
                        data-testid="buffer-mode-select"
                      >
                        <option value="addToQueue">Add to Buffer queue</option>
                        <option value="customScheduled">Schedule for specific time</option>
                        <option value="shareNow">Publish now</option>
                      </select>
                    </div>
                    {publishMode === "customScheduled" && (
                      <div>
                        <Label className="text-[11px] text-slate-500">When</Label>
                        <input
                          type="datetime-local"
                          value={publishDueAt}
                          onChange={(e) => setPublishDueAt(e.target.value)}
                          className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs"
                          data-testid="buffer-due-at"
                        />
                      </div>
                    )}
                  </div>
                  {publishResult && (
                    <div className="text-xs space-y-1 mt-2">
                      {(publishResult.results || []).map((r, i) => (
                        <div key={i} className={`rounded px-2 py-1 ${r.success ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}>
                          {r.success
                            ? <>✓ {bufferChannels.find(c => c.id === r.channel_id)?.name || r.channel_id} — {r.post?.status} {r.post?.dueAt ? `· ${new Date(r.post.dueAt).toLocaleString()}` : ""}</>
                            : <>✗ {bufferChannels.find(c => c.id === r.channel_id)?.name || r.channel_id} — {r.error}</>}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Manual outcome entry (LinkedIn analytics) */}
            <OutcomeEntry workspaceId={workspaceId} cardId={cardId} />
            </>
            )}

            {/* REX-004D — Approval audit trail. Permanently visible on the card.
                Banner answers "who approved this · when · why" at a glance;
                Timeline below renders every request_approval / approve / reject
                event with attached_approvers (shared-ownership snapshot). */}
            {(audit.events.length > 0 || card.stage === "PENDING_APPROVAL") && (
              <div className="space-y-2" data-testid="drawer-approval-audit">
                <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <Shield className="h-4 w-4"/>Approval trail ({audit.events.length})
                </h4>

                {/* Latest-decision banner */}
                {audit.latest_decision && (() => {
                  const ld = audit.latest_decision;
                  const isApproved = ld.decision === "APPROVED";
                  const tone = isApproved
                    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                    : "bg-rose-50 border-rose-200 text-rose-900";
                  const dt = ld.at ? new Date(ld.at) : null;
                  const stamp = dt ? dt.toLocaleString(undefined, { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
                  return (
                    <div className={`rounded-lg border-2 px-3 py-2 ${tone}`} data-testid="drawer-decision-banner">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-bold">
                            {isApproved ? "Approved" : "Rejected"} by {ld.actor_name || ld.actor_email}
                          </div>
                          <div className="text-[11px] opacity-80">{stamp}</div>
                          {ld.reason && <div className="text-xs mt-1"><span className="font-semibold">Reason:</span> {ld.reason}</div>}
                        </div>
                        {/* Next Steps prompt — only after an Approve. Surfaces
                            common follow-ups so users don't have to leave the
                            drawer to action the card. */}
                        {isApproved && (
                          <Button size="sm" variant="outline" className="bg-white" onClick={() => setShowNextSteps((v) => !v)} data-testid="drawer-next-steps-toggle">
                            Next Steps?
                          </Button>
                        )}
                      </div>
                      {isApproved && showNextSteps && (
                        <div className="mt-3 pt-3 border-t border-emerald-200 flex flex-wrap gap-2" data-testid="drawer-next-steps">
                          <Button size="sm" variant="outline" onClick={async () => {
                            const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/transition`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ target_stage: "SCHEDULED", expected_version: card.version }) });
                            if (r.ok) { toast.success("Scheduled"); setShowNextSteps(false); load(); onMutated && onMutated(); }
                            else toast.error("Couldn't schedule");
                          }} data-testid="drawer-next-schedule"><Send className="h-3 w-3 mr-1"/>Schedule</Button>
                          <Button size="sm" variant="outline" onClick={async () => {
                            const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/transition`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ target_stage: "PUBLISHED", expected_version: card.version }) });
                            if (r.ok) { toast.success("Marked Published"); setShowNextSteps(false); load(); onMutated && onMutated(); }
                            else toast.error("Couldn't mark published");
                          }} data-testid="drawer-next-publish">Mark Published</Button>
                          <Button size="sm" variant="outline" onClick={async () => {
                            const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/transition`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ target_stage: "IN_DESIGN", expected_version: card.version, note: "Returned for revision after approval" }) });
                            if (r.ok) { toast.success("Returned to Design"); setShowNextSteps(false); load(); onMutated && onMutated(); }
                            else toast.error("Couldn't return");
                          }} data-testid="drawer-next-return"><RotateCcw className="h-3 w-3 mr-1"/>Return for Revision</Button>
                          <Button size="sm" variant="outline" onClick={async () => {
                            const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/transition`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ target_stage: "ARCHIVED", expected_version: card.version }) });
                            if (r.ok) { toast.success("Archived"); setShowNextSteps(false); load(); onMutated && onMutated(); }
                            else toast.error("Couldn't archive");
                          }} data-testid="drawer-next-archive"><Trash2 className="h-3 w-3 mr-1"/>Archive</Button>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Timeline (oldest → newest). Each event captures actor, decision,
                    reason and the snapshot of attached_approvers — answering
                    "who could have acted alongside the actual decision-maker?". */}
                <div className="rounded-md border border-slate-200 bg-white divide-y divide-slate-100 max-h-72 overflow-y-auto">
                  {audit.events.length === 0 ? (
                    <p className="p-3 text-xs text-slate-500">Awaiting first approval action…</p>
                  ) : (
                    audit.events.map((ev) => {
                      const dt = ev.at ? new Date(ev.at) : null;
                      const stamp = dt ? dt.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
                      const label = ev.action === "request_approval" ? "Sent for approval"
                        : ev.action === "approve" ? "Approved"
                        : ev.action === "reject" ? "Rejected"
                        : ev.action;
                      const tone = ev.action === "approve" ? "text-emerald-700"
                        : ev.action === "reject" ? "text-rose-700"
                        : "text-slate-700";
                      return (
                        <div key={ev.event_id} className="px-3 py-2 text-xs" data-testid={`drawer-audit-event-${ev.event_id}`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className={`font-semibold ${tone}`}>{label} · {ev.actor_name || ev.actor_email}</span>
                            <span className="text-slate-500">{stamp}</span>
                          </div>
                          {ev.reason && (
                            <div className="text-slate-600 mt-0.5"><span className="font-semibold">Reason:</span> {ev.reason}</div>
                          )}
                          {ev.action === "request_approval" && Array.isArray(ev.attached_approvers) && ev.attached_approvers.length > 0 && (
                            <div className="text-slate-500 mt-0.5">Routed to: {ev.attached_approvers.join(", ")}</div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* Stage history — the full timeline of when this card moved
                between stages, who moved it, and how long it sat. */}
            <div className="space-y-2" data-testid="drawer-stage-history">
              <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <Clock className="h-4 w-4"/>Stage history ({stageHistory.history.length})
              </h4>
              {stageHistory.stage_entered_at && (
                <p className="text-xs text-slate-600" data-testid="drawer-time-in-stage">
                  Currently <span className="font-semibold">{STAGE_LABEL[card.stage] || card.stage}</span> · in this stage for{" "}
                  <span className="font-semibold">{durationSince(stageHistory.stage_entered_at)}</span>
                  {" "}(since {new Date(stageHistory.stage_entered_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })})
                </p>
              )}
              <div className="rounded-md border border-slate-200 bg-white divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {stageHistory.history.length === 0 ? (
                  <p className="p-3 text-sm text-slate-500">No stage changes recorded yet.</p>
                ) : (
                  [...stageHistory.history].reverse().map((h, i) => {
                    const total = stageHistory.history.length;
                    const stepNo = total - i; // newest first
                    return (
                      <div key={`${h.at}-${i}`} className="p-3" data-testid={`drawer-stage-step-${stepNo}`}>
                        <div className="flex items-center gap-1.5 flex-wrap text-xs">
                          {h.from_stage ? (
                            <>
                              <span className={`rounded px-1.5 py-[1px] border ${STAGE_BADGE[h.from_stage] || "bg-slate-100 text-slate-700 border-slate-300"}`}>
                                {STAGE_LABEL[h.from_stage] || h.from_stage}
                              </span>
                              <ArrowRight className="h-3 w-3 text-slate-400" />
                            </>
                          ) : null}
                          <span className={`rounded px-1.5 py-[1px] border font-medium ${STAGE_BADGE[h.to_stage] || "bg-slate-100 text-slate-700 border-slate-300"}`}>
                            {STAGE_LABEL[h.to_stage] || h.to_stage}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {h.actor_email || "—"} · {h.at ? new Date(h.at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                        </p>
                        {h.note && h.note !== "Drag-and-drop" && (
                          <p className="text-xs text-slate-600 mt-0.5 italic">“{h.note}”</p>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Version history */}
            <div className="space-y-2" data-testid="drawer-versions">
              <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><History className="h-4 w-4"/>Version history ({versions.length})</h4>
              <div className="rounded-md border border-slate-200 bg-white divide-y divide-slate-100 max-h-80 overflow-y-auto">
                {versions.length === 0 ? <p className="p-3 text-sm text-slate-500">No snapshots yet.</p> :
                  versions.map((v) => (
                    <div key={v.id} className="flex items-center justify-between p-3" data-testid={`drawer-version-${v.version}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-slate-900">
                          <span className="font-medium">v{v.version}</span>
                          <span className="text-slate-500 ml-2">{v.reason}</span>
                        </p>
                        <p className="text-xs text-slate-500">
                          {v.actor_email || "—"} · {new Date(v.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                        </p>
                      </div>
                      {v.version !== card.version && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => restoreVersion(v.version)}
                          disabled={restoring === v.version}
                          data-testid={`drawer-restore-v${v.version}`}
                        >
                          {restoring === v.version ? <Loader2 className="h-3 w-3 animate-spin"/> : <><RotateCcw className="h-3 w-3 mr-1"/>Restore</>}
                        </Button>
                      )}
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DriftBadge({ score }) {
  const pct = Math.round((score || 0) * 100);
  let cls = "bg-emerald-100 text-emerald-900 border-emerald-300";
  if (score > 0.25) cls = "bg-amber-100 text-amber-900 border-amber-300";
  if (score > 0.55) cls = "bg-red-100 text-red-900 border-red-300";
  return <Badge className={cls} data-testid="drift-badge">Drift {pct}%</Badge>;
}

function ManualVerdictBadge({ score }) {
  const s = score || 0;
  if (s <= 0.15) return <Badge className="bg-emerald-500 text-white border-emerald-600" data-testid="manual-verdict">✓ PASS</Badge>;
  if (s <= 0.45) return <Badge className="bg-amber-500 text-white border-amber-600" data-testid="manual-verdict">⚠ WARN</Badge>;
  return <Badge className="bg-red-500 text-white border-red-600" data-testid="manual-verdict">✗ FAIL</Badge>;
}



function OutcomeEntry({ workspaceId, cardId }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    platform: "linkedin",
    impressions: "",
    reactions: "",
    comments: "",
    shares: "",
    clicks: "",
    reach: "",
    leads: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    const body = {};
    ["platform","impressions","reactions","comments","shares","clicks","reach","leads"].forEach(k => {
      if (form[k] !== "") body[k] = k === "platform" ? form[k] : Number(form[k]);
    });
    const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/outcomes`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      credentials: "include", body: JSON.stringify(body),
    });
    setSaving(false);
    if (r.ok) {
      const d = await r.json();
      toast.success(`Outcome saved · ER ${d.engagement_rate ? (d.engagement_rate*100).toFixed(2)+"%" : "—"}`);
      setOpen(false);
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Save failed");
    }
  };
  return (
    <div className="space-y-2 rounded-md border border-slate-200 bg-white p-3" data-testid="outcome-entry">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Gauge className="h-4 w-4"/>Post-publish metrics</h4>
        <Button size="sm" variant="ghost" onClick={() => setOpen(o => !o)} data-testid="outcome-toggle-btn">
          {open ? "Cancel" : "Log metrics"}
        </Button>
      </div>
      <p className="text-[11px] text-slate-500">Buffer's API doesn't expose engagement metrics. Paste your LinkedIn post analytics here so Rex can learn what works.</p>
      {open && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2">
          {[["impressions","Impressions"],["reactions","Reactions"],["comments","Comments"],["shares","Shares"],["clicks","Clicks"],["reach","Reach"],["leads","Leads"]].map(([k, l]) => (
            <div key={k}>
              <Label className="text-[11px] text-slate-500">{l}</Label>
              <Input type="number" value={form[k]} onChange={(e) => set(k, e.target.value)} placeholder="0" data-testid={`outcome-${k}`} />
            </div>
          ))}
          <div className="col-span-2 md:col-span-4 flex justify-end">
            <Button size="sm" onClick={save} disabled={saving} data-testid="outcome-save-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : null} Save metrics
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}



// REX-DESIGN-LIBRARY follow-up — Visual brand-guard panel.
// Calls /image/brand-check which compares the card's current image against
// top-3 references in the design library and returns drift score + issues.
function VisualBrandCheck({ workspaceId, cardId }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    setRunning(true); setError(null);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/brand-check`,
        { method: "POST", credentials: "include" }
      );
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail || `Failed (${r.status})`);
      setResult(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const verdictCls = (v) => v === "on_brand"
    ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : v === "minor_drift"
      ? "bg-amber-100 text-amber-900 border-amber-300"
      : v === "off_brand"
        ? "bg-rose-100 text-rose-800 border-rose-300"
        : "bg-slate-100 text-slate-700 border-slate-300";

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-2" data-testid="visual-brand-check">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-xs font-semibold text-slate-800">Visual brand check</p>
          <p className="text-[11px] text-slate-500">
            Compare against top-3 references from your design library (filtered by voice).
          </p>
        </div>
        <Button size="sm" onClick={run} disabled={running} data-testid="visual-brand-check-run">
          {running ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
          {result ? "Re-run check" : "Check brand fit"}
        </Button>
      </div>
      {error && <p className="text-xs text-rose-600">{error}</p>}
      {result && (
        <div className="space-y-2 text-xs" data-testid="visual-brand-check-result">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wider font-bold ${verdictCls(result.verdict)}`}>
              {(result.verdict || "").replace(/_/g, " ")}
            </Badge>
            {result.drift_score != null && (
              <span className="text-[11px] text-slate-700">
                Drift score: <strong>{Math.round(result.drift_score * 100)}%</strong>
              </span>
            )}
            {result.references_used != null && (
              <span className="text-[11px] text-slate-500">
                vs {result.references_used} reference{result.references_used === 1 ? "" : "s"}
              </span>
            )}
            {result.confidence && (
              <span className="text-[11px] text-slate-400">
                · {result.confidence} confidence
              </span>
            )}
          </div>
          {result.rationale && (
            <p className="text-[12px] text-slate-700 italic bg-white border border-slate-200 rounded px-2 py-1.5">
              "{result.rationale}"
            </p>
          )}
          {result.issues && result.issues.length > 0 && (
            <ul className="space-y-1" data-testid="visual-brand-check-issues">
              {result.issues.map((i, idx) => (
                <li key={idx} className="flex items-start gap-2 text-[11px]" data-testid={`visual-brand-check-issue-${idx}`}>
                  <span className={`text-[9px] uppercase tracking-wider rounded px-1.5 py-[1px] border flex-shrink-0 ${
                    i.severity === "high" ? "bg-rose-100 text-rose-800 border-rose-300" :
                    i.severity === "medium" ? "bg-amber-100 text-amber-900 border-amber-300" :
                    "bg-slate-100 text-slate-600 border-slate-300"
                  }`}>{i.severity}</span>
                  <div>
                    <span className="font-semibold capitalize">{i.area}: </span>
                    <span className="text-slate-700">{i.note}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
